package com.heelion.farmafreelance.ui

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.Image
import androidx.compose.foundation.border
import androidx.compose.foundation.relocation.BringIntoViewRequester
import androidx.compose.foundation.relocation.bringIntoViewRequester
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import com.heelion.farmafreelance.FarmaFreelanceBlue
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.PopupProperties
import kotlinx.datetime.*
import farmafreelance.composeapp.generated.resources.*
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import com.heelion.farmafreelance.models.Farmacia
import com.heelion.farmafreelance.models.Farmacista
import com.heelion.farmafreelance.models.Infermiere
import com.heelion.farmafreelance.models.UserRole
import com.heelion.farmafreelance.models.isProfessionista
import com.heelion.farmafreelance.utils.ItalianCfUtils
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class, ExperimentalFoundationApi::class)
@Composable
fun RegisterScreen(
    viewModel: AuthViewModel,
    onBackToLogin: () -> Unit,
    onSettingsClick: () -> Unit = {},
    currentLanguage: String = "it",
    onLanguageChange: (String) -> Unit = {}
) {
    var role by remember { mutableStateOf(UserRole.FARMACISTA) }
    var consensoMarketing by remember { mutableStateOf(false) } // facoltativo, mai preselezionato
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    
    // Campi Farmacista
    var nome by remember { mutableStateOf("") }
    var cognome by remember { mutableStateOf("") }
    var dataNascita by remember { mutableStateOf("") }
    var luogoNascita by remember { mutableStateOf("") }
    var sesso by remember { mutableStateOf("M") }
    var cittadinanza by remember { mutableStateOf("Italiana") }
    var codiceFiscale by remember { mutableStateOf("") }
    var numeroIscrizioneOrdine by remember { mutableStateOf("") }

    // Campi Fiscali Farmacista
    var regimeFiscale by remember { mutableStateOf("FORFETTARIO") }
    var percEnpaf by remember { mutableStateOf("2.0") }
    
    // Solo infermieri: polizza RC professionale (obbligatoria per lavorare, l'admin la verifica)
    var polizzaRcNumero by remember { mutableStateOf("") }
    var polizzaRcScadenza by remember { mutableStateOf("") }

    // Competenze
    val competenzePredefinite = competenzeDi(role)
    val selectedSkills = remember { mutableStateListOf<String>() }
    var showOtherSkillField by remember { mutableStateOf(false) }
    var otherSkillText by remember { mutableStateOf("") }
    var expandedCompetenze by remember { mutableStateOf(false) }
    
    // Campi Farmacia
    var nomeFarmacia by remember { mutableStateOf("") }
    var ragioneSociale by remember { mutableStateOf("") }
    var partitaIva by remember { mutableStateOf("") }
    var codiceSDI by remember { mutableStateOf("") }
    var pec by remember { mutableStateOf("") }
    var numeroIscrizioneOrdineDirettore by remember { mutableStateOf("") }
    var indirizzo by remember { mutableStateOf("") }
    var cap by remember { mutableStateOf("") }
    var citta by remember { mutableStateOf("") }
    var provincia by remember { mutableStateOf("") }
    var regione by remember { mutableStateOf("") }
    var telefono by remember { mutableStateOf("") }

    // Stati per i menu a tendina
    var expRegione by remember { mutableStateOf(false) }
    var expProvincia by remember { mutableStateOf(false) }
    var expComune by remember { mutableStateOf(false) }
    var expCap by remember { mutableStateOf(false) }

    var suggerimentiComuni by remember { mutableStateOf<List<String>>(emptyList()) }
    var showSuggerimenti by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    val scrollState = rememberScrollState()
    var errorMsgLocal by remember { mutableStateOf<String?>(null) }
    
    // Stati per il DatePicker
    var showDatePicker by remember { mutableStateOf(false) }
    val datePickerState = rememberDatePickerState()
    
    // Cambiando professione cambiano cassa (ENPAF 2%, ENPAPI 4%) e competenze proposte.
    LaunchedEffect(role) {
        percEnpaf = if (role == UserRole.INFERMIERE) "4.0" else "2.0"
        selectedSkills.clear()
    }

    val taxCodeErrorLength = stringResource(Res.string.tax_code_error_length)
    val taxCodeErrorInvalid = stringResource(Res.string.tax_code_error_invalid)

    // Iscrizione in 4 passaggi con Farmy (vedi RegistrazioneFarmy): passaggio attuale, campo
    // toccato (per l'aiuto) e cosa manca per andare avanti.
    var passo by remember { mutableStateOf(0) }
    var campoAiuto by remember { mutableStateOf<String?>(null) }
    var avvisoFarmy by remember { mutableStateOf<String?>(null) }
    val voceFarmy = LocalAssistente.current.voce
    var voceAttiva by remember { mutableStateOf(true) }
    DisposableEffect(Unit) { onDispose { voceFarmy.stop() } } // uscendo dall'iscrizione Farmy tace
    // Livello 1: campi obbligatori mancanti dopo "Avanti" (rossi) e quello da cui ripartire (lampeggia).
    var campiInErrore by remember { mutableStateOf<Set<String>>(emptySet()) }
    var campoLampeggiante by remember { mutableStateOf<String?>(null) }
    // Livello 2: "Guidami tu", Farmy evidenzia un campo alla volta finché non è compilato.
    var guida by remember { mutableStateOf(false) }

    val cfPulito = codiceFiscale.trim().uppercase().filter { it.isLetterOrDigit() }
    /** true se il campo [chiave] è compilato (per il codice fiscale: valido). */
    fun compilato(chiave: String): Boolean = when (chiave) {
        "nome" -> nome.isNotBlank()
        "cognome" -> cognome.isNotBlank()
        "dataNascita" -> dataNascita.isNotBlank()
        "luogoNascita" -> luogoNascita.isNotBlank()
        "cittadinanza" -> cittadinanza.isNotBlank()
        "codiceFiscale" -> cfPulito.length == 16 && ItalianCfUtils.isCodiceFiscaleValido(cfPulito)
        "iscrizioneOrdine" -> numeroIscrizioneOrdine.isNotBlank()
        "competenze" -> selectedSkills.isNotEmpty()
        "zona" -> if (role.isProfessionista) regione.isNotBlank()
            else regione.isNotBlank() && provincia.isNotBlank() && citta.isNotBlank() && cap.isNotBlank()
        "telefono" -> telefono.isNotBlank()
        "nomeFarmacia" -> nomeFarmacia.isNotBlank()
        "ragioneSociale" -> ragioneSociale.isNotBlank()
        "partitaIva" -> partitaIva.isNotBlank()
        "sdi" -> codiceSDI.isNotBlank()
        "pec" -> pec.isNotBlank()
        "direttore" -> numeroIscrizioneOrdineDirettore.isNotBlank()
        "indirizzo" -> indirizzo.isNotBlank()
        "polizzaRc" -> polizzaRcNumero.isNotBlank()
        "email" -> email.contains("@")
        "password" -> password.length >= 6
        else -> true
    }
    val campiPasso = RegistrazioneFarmy.campi(role, passo)
    /** Obbligatori mancanti del passaggio, nell'ordine in cui compaiono. */
    fun mancanti(): List<String> = campiPasso.filter { it.obbligatorio && !compilato(it.chiave) }.map { it.chiave }
    // Con la guida: il campo evidenziato ora. Passa al successivo quando lo si lascia compilato o
    // con "Avanti" nel riquadro di Farmy; i facoltativi saltati non vengono riproposti.
    var campoGuidato by remember { mutableStateOf<String?>(null) }
    var saltati by remember { mutableStateOf<Set<String>>(emptySet()) }
    /** Prima gli obbligatori da compilare, poi i facoltativi non ancora saltati. */
    fun prossimoGuidato(): String? =
        (campiPasso.firstOrNull { it.obbligatorio && !compilato(it.chiave) }
            ?: campiPasso.firstOrNull { !it.obbligatorio && !compilato(it.chiave) && it.chiave !in saltati })?.chiave
    fun avanzaGuida() {
        campoGuidato?.let { attuale -> if (!compilato(attuale)) saltati = saltati + attuale }
        campoGuidato = prossimoGuidato()
    }
    LaunchedEffect(guida, passo, role) { campoGuidato = if (guida) prossimoGuidato() else null }
    // La zona si sceglie dai menu a tendina (niente "uscita" dal campo): appena completa, la guida va avanti.
    LaunchedEffect(campoGuidato, regione, provincia, citta, cap) {
        if (campoGuidato == "zona" && compilato("zona")) avanzaGuida()
    }
    // Anche le competenze sono un menu: la guida lo apre e va avanti quando lo si chiude con almeno una scelta.
    LaunchedEffect(campoGuidato) { if (campoGuidato == "competenze") expandedCompetenze = true }
    LaunchedEffect(campoGuidato, expandedCompetenze, selectedSkills.size) {
        if (campoGuidato == "competenze" && !expandedCompetenze && compilato("competenze")) avanzaGuida()
    }

    // Per portare la pagina su un campo e attivarlo.
    val richiesteFocus = remember { mutableMapOf<String, FocusRequester>() }
    val richiesteVista = remember { mutableMapOf<String, BringIntoViewRequester>() }
    fun focusDi(chiave: String) = richiesteFocus.getOrPut(chiave) { FocusRequester() }
    fun vistaDi(chiave: String) = richiesteVista.getOrPut(chiave) { BringIntoViewRequester() }
    fun portaA(chiave: String) = scope.launch {
        try { vistaDi(chiave).bringIntoView() } catch (_: Exception) {}
        try { focusDi(chiave).requestFocus() } catch (_: Exception) {} // campi a tendina: non sempre focalizzabili
    }
    val lampeggio = rememberLampeggio(campoLampeggiante != null || campoGuidato != null)
    LaunchedEffect(campoLampeggiante) {
        if (campoLampeggiante != null) { kotlinx.coroutines.delay(4_000); campoLampeggiante = null }
    }
    // La guida passa al campo successivo appena quello attuale è compilato.
    LaunchedEffect(campoGuidato) { campoGuidato?.let { portaA(it) } }

    /**
     * Ogni campo dell'iscrizione: aiuto di Farmy quando lo si tocca, raggiungibile dalla guida
     * e dagli errori, bordo evidenziato quando è il campo guidato o quello da cui ripartire.
     */
    @Composable
    fun Modifier.campo(chiave: String): Modifier {
        val colore = when (chiave) {
            campoLampeggiante -> Color(0xFFD32F2F)
            campoGuidato -> FarmaFreelanceBlue
            else -> null
        }
        return this
            .bringIntoViewRequester(vistaDi(chiave))
            .focusRequester(focusDi(chiave))
            .onFocusChanged {
                if (it.isFocused) campoAiuto = chiave
                // Lasciato il campo guidato dopo averlo compilato: la guida va al successivo.
                else if (chiave == campoGuidato && compilato(chiave)) avanzaGuida()
            }
            .then(if (colore != null) Modifier.border(3.dp, colore.copy(alpha = 0.3f + 0.7f * lampeggio), RoundedCornerShape(12.dp)) else Modifier)
    }
    /** Campo mancante dopo "Avanti": bordo rosso e "Da compilare" sotto. */
    fun inErrore(chiave: String) = chiave in campiInErrore && !compilato(chiave)
    fun daCompilare(chiave: String): (@Composable () -> Unit)? =
        if (inErrore(chiave)) { { Text(if (chiave == "codiceFiscale" && cfPulito.length == 16) "Codice fiscale non valido" else "Da compilare") } } else null

    /** Livello 1: manca qualcosa → rossi, Farmy dice cosa, pagina sul primo che lampeggia. */
    fun segnalaMancanti(manca: List<String>, perCompletare: Boolean) {
        campiInErrore = manca.toSet()
        avvisoFarmy = (if (perCompletare) "Per completare mi " else "Per andare avanti mi ") +
            (if (manca.size == 1) "manca " else "mancano ") + manca.joinToString(", ") { RegistrazioneFarmy.etichetta(it, role) } + "."
        campoAiuto = null
        campoLampeggiante = manca.first()
        portaA(manca.first())
    }
    fun cambiaPasso(nuovo: Int) {
        passo = nuovo
        campoAiuto = null
        avvisoFarmy = null
        campiInErrore = emptySet()
        campoLampeggiante = null
        scope.launch { scrollState.animateScrollTo(0) }
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(24.dp)
            .statusBarsPadding()
            .verticalScroll(scrollState),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Icona Impostazioni (Ingranaggio) in alto a destra - Apre direttamente il dettaglio
        Box(modifier = Modifier.fillMaxWidth()) {
            IconButton(
                onClick = onSettingsClick,
                modifier = Modifier.align(Alignment.CenterEnd)
            ) {
                Icon(
                    imageVector = AppIcons.Settings,
                    contentDescription = "Settings",
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(28.dp)
                )
            }
        }
        Image(
            painter = painterResource(Res.drawable.farmafreelance_logo),
            contentDescription = "FarmaFreelance Logo",
            modifier = Modifier.height(50.dp).padding(bottom = 16.dp)
        )

        Text(stringResource(Res.string.register_title_screen), style = MaterialTheme.typography.headlineMedium, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
        
        Spacer(modifier = Modifier.height(24.dp))
        
        // Farmacista, infermiere o farmacia si sceglie solo all'inizio: i passaggi dopo dipendono da questo.
        if (passo == 0) {
            FlowRow(verticalArrangement = Arrangement.Center, modifier = Modifier.fillMaxWidth()) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    RadioButton(selected = role == UserRole.FARMACISTA, onClick = { role = UserRole.FARMACISTA; cambiaPasso(0) })
                    Text(stringResource(Res.string.farmacista))
                    Spacer(modifier = Modifier.width(12.dp))
                }
                Row(verticalAlignment = Alignment.CenterVertically) {
                    RadioButton(selected = role == UserRole.INFERMIERE, onClick = { role = UserRole.INFERMIERE; cambiaPasso(0) })
                    Text("Infermiere")
                    Spacer(modifier = Modifier.width(12.dp))
                }
                Row(verticalAlignment = Alignment.CenterVertically) {
                    RadioButton(selected = role == UserRole.FARMACIA, onClick = { role = UserRole.FARMACIA; cambiaPasso(0) })
                    Text(stringResource(Res.string.farmacia))
                }
            }
            Spacer(modifier = Modifier.height(8.dp))
        }

        // Con la guida Farmy parla del campo evidenziato; se no del campo toccato o del passaggio.
        val guidato = campoGuidato?.let { k -> campiPasso.firstOrNull { it.chiave == k } }
        val testoFarmy = when {
            guidato != null -> RegistrazioneFarmy.testoGuida(guidato.chiave, role, guidato.obbligatorio)
            guida -> "Questo passaggio è completo: premi Avanti."
            else -> campoAiuto?.let { RegistrazioneFarmy.aiuto(it, role) } ?: RegistrazioneFarmy.introduzione(role, passo)
        }
        // Farmy parla da solo quando cambia quello che dice (nuovo passaggio, campo toccato o
        // guidato, cosa manca). La breve attesa evita di iniziare frasi subito interrotte.
        LaunchedEffect(testoFarmy, avvisoFarmy, voceAttiva) {
            if (!voceAttiva) { voceFarmy.stop(); return@LaunchedEffect }
            kotlinx.coroutines.delay(400)
            voceFarmy.speak(avvisoFarmy ?: testoFarmy)
        }
        RiquadroFarmyIscrizione(
            passo = passo,
            titolo = RegistrazioneFarmy.titolo(role, passo),
            testo = testoFarmy,
            avviso = avvisoFarmy,
            voceAttiva = if (voceFarmy.isAvailable()) voceAttiva else null,
            onVoce = { voceAttiva = it },
            guida = guida,
            onGuida = { guida = it; avvisoFarmy = null },
            campoGuidatoFacoltativo = guidato?.let { !it.obbligatorio && !compilato(it.chiave) } ?: false,
            onCampoSuccessivo = if (guidato != null) { { avanzaGuida() } } else null
        )
        Spacer(modifier = Modifier.height(16.dp))

        if (role.isProfessionista) {
          if (passo == 0) {
            OutlinedTextField(value = nome, onValueChange = { nome = it }, label = { Text(stringResource(Res.string.name_label) + " *") }, modifier = Modifier.fillMaxWidth().campo("nome"), shape = RoundedCornerShape(12.dp), isError = inErrore("nome"), supportingText = daCompilare("nome"))
            Spacer(modifier = Modifier.height(8.dp))
            OutlinedTextField(value = cognome, onValueChange = { cognome = it }, label = { Text(stringResource(Res.string.surname_label) + " *") }, modifier = Modifier.fillMaxWidth().campo("cognome"), shape = RoundedCornerShape(12.dp), isError = inErrore("cognome"), supportingText = daCompilare("cognome"))
            Spacer(modifier = Modifier.height(8.dp))
            
            Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Text(stringResource(Res.string.gender_label), style = MaterialTheme.typography.bodyMedium)
                RadioButton(selected = sesso == "M", onClick = { sesso = "M" })
                Text(stringResource(Res.string.male_gender))
                Spacer(modifier = Modifier.width(16.dp))
                RadioButton(selected = sesso == "F", onClick = { sesso = "F" })
                Text(stringResource(Res.string.female_gender))
            }
            Spacer(modifier = Modifier.height(8.dp))

            Spacer(modifier = Modifier.height(8.dp))

            if (showDatePicker) {
                DatePickerDialog(
                    onDismissRequest = { showDatePicker = false },
                    confirmButton = {
                        TextButton(onClick = {
                            datePickerState.selectedDateMillis?.let { millis ->
                                val date = Instant.fromEpochMilliseconds(millis).toLocalDateTime(TimeZone.UTC).date
                                dataNascita = "${date.dayOfMonth.toString().padStart(2, '0')}/${date.monthNumber.toString().padStart(2, '0')}/${date.year}"
                            }
                            showDatePicker = false
                        }) {
                            Text(stringResource(Res.string.ok_button))
                        }
                    },
                    dismissButton = {
                        TextButton(onClick = { showDatePicker = false }) {
                            Text(stringResource(Res.string.cancel_button))
                        }
                    }
                ) {
                    DatePicker(state = datePickerState)
                }
            }

            OutlinedTextField(
                value = dataNascita, 
                onValueChange = { dataNascita = it }, 
                label = { Text(stringResource(Res.string.birth_date_label)) },
                modifier = Modifier.fillMaxWidth().campo("dataNascita"),
                shape = RoundedCornerShape(12.dp),
                isError = inErrore("dataNascita"),
                supportingText = daCompilare("dataNascita"),
                trailingIcon = {
                    IconButton(onClick = { showDatePicker = true }) {
                        Icon(Icons.Default.DateRange, contentDescription = "Select Date")
                    }
                }
            )
            Spacer(modifier = Modifier.height(8.dp))
            
            Box(modifier = Modifier.fillMaxWidth()) {
                OutlinedTextField(
                    value = luogoNascita,
                    onValueChange = { 
                        luogoNascita = it
                        if (it.length >= 3) {
                            scope.launch {
                                suggerimentiComuni = viewModel.searchComuni(it)
                                showSuggerimenti = suggerimentiComuni.isNotEmpty()
                            }
                        } else {
                            showSuggerimenti = false
                        }
                    },
                    label = { Text(stringResource(Res.string.birth_place_label)) },
                    modifier = Modifier.fillMaxWidth().campo("luogoNascita"),
                    shape = RoundedCornerShape(12.dp),
                    isError = inErrore("luogoNascita"),
                    supportingText = daCompilare("luogoNascita")
                )
                if (showSuggerimenti && suggerimentiComuni.isNotEmpty()) {
                    DropdownMenu(expanded = showSuggerimenti, onDismissRequest = { showSuggerimenti = false }, properties = PopupProperties(focusable = false)) {
                        suggerimentiComuni.forEach { comuneStr ->
                            DropdownMenuItem(
                                text = { Text(comuneStr) }, 
                                onClick = { luogoNascita = comuneStr; showSuggerimenti = false }
                            )
                        }
                    }
                }
            }
            Spacer(modifier = Modifier.height(8.dp))
            OutlinedTextField(value = cittadinanza, onValueChange = { cittadinanza = it }, label = { Text(stringResource(Res.string.citizenship_label)) }, modifier = Modifier.fillMaxWidth().campo("cittadinanza"), shape = RoundedCornerShape(12.dp), isError = inErrore("cittadinanza"), supportingText = daCompilare("cittadinanza"))
            Spacer(modifier = Modifier.height(8.dp))
            OutlinedTextField(value = codiceFiscale, onValueChange = { codiceFiscale = it }, label = { Text(stringResource(Res.string.tax_code_label)) }, modifier = Modifier.fillMaxWidth().campo("codiceFiscale"), shape = RoundedCornerShape(12.dp), isError = inErrore("codiceFiscale"), supportingText = daCompilare("codiceFiscale"))
          }
          if (passo == 1) {
            OutlinedTextField(
                value = numeroIscrizioneOrdine,
                onValueChange = { numeroIscrizioneOrdine = it },
                label = { Text(if (role == UserRole.INFERMIERE) "N. iscrizione OPI" else stringResource(Res.string.order_registration_label)) },
                modifier = Modifier.fillMaxWidth().campo("iscrizioneOrdine"),
                shape = RoundedCornerShape(12.dp)
            )
            if (role == UserRole.INFERMIERE) {
                Spacer(modifier = Modifier.height(8.dp))
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlinedTextField(
                        value = polizzaRcNumero,
                        onValueChange = { polizzaRcNumero = it },
                        label = { Text("N. polizza RC") },
                        modifier = Modifier.weight(1f).campo("polizzaRc"),
                        shape = RoundedCornerShape(12.dp),
                        singleLine = true
                    )
                    OutlinedTextField(
                        value = polizzaRcScadenza,
                        onValueChange = { polizzaRcScadenza = it },
                        label = { Text("Scadenza (gg/mm/aaaa)") },
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(12.dp),
                        singleLine = true
                    )
                }
            }
            Spacer(modifier = Modifier.height(16.dp))

            // --- DATI FISCALI ---
            Text("Dati Fiscali (Opzionali)", style = MaterialTheme.typography.titleSmall, color = MaterialTheme.colorScheme.primary, fontWeight = FontWeight.Bold, modifier = Modifier.align(Alignment.Start))
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                EditableDropdownField(
                    label = "Regime Fiscale",
                    value = regimeFiscale,
                    onValueChange = { regimeFiscale = it },
                    options = listOf("FORFETTARIO", "ORDINARIO", "MINIMI"),
                    modifier = Modifier.weight(1f).campo("regimeFiscale")
                )
                OutlinedTextField(
                    value = percEnpaf,
                    onValueChange = { percEnpaf = it },
                    label = { Text(if (role == UserRole.INFERMIERE) "% ENPAPI" else "% ENPAF") },
                    modifier = Modifier.weight(0.5f).campo("enpaf"),
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                    shape = RoundedCornerShape(12.dp)
                )
            }
            Spacer(modifier = Modifier.height(16.dp))

            // --- SEZIONE COMPETENZE ---
            Text("Competenze Professionali", style = MaterialTheme.typography.titleSmall, color = MaterialTheme.colorScheme.primary, fontWeight = FontWeight.Bold, modifier = Modifier.align(Alignment.Start))
            Spacer(modifier = Modifier.height(8.dp))
            
            ExposedDropdownMenuBox(
                expanded = expandedCompetenze,
                onExpandedChange = { expandedCompetenze = !expandedCompetenze; campoAiuto = "competenze" },
                modifier = Modifier.fillMaxWidth()
            ) {
                OutlinedTextField(
                    value = if (selectedSkills.isEmpty()) "Seleziona competenze..." else "${selectedSkills.size} selezionate",
                    onValueChange = {},
                    readOnly = true,
                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expandedCompetenze) },
                    modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth().campo("competenze"),
                    shape = RoundedCornerShape(12.dp)
                )
                ExposedDropdownMenu(expanded = expandedCompetenze, onDismissRequest = { expandedCompetenze = false }) {
                    competenzePredefinite.forEach { skill ->
                        val isSelected = selectedSkills.contains(skill)
                        DropdownMenuItem(
                            text = {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Checkbox(checked = isSelected, onCheckedChange = null)
                                    Spacer(Modifier.width(8.dp))
                                    Text(skill)
                                }
                            },
                            onClick = {
                                if (isSelected) selectedSkills.remove(skill) else selectedSkills.add(skill)
                            }
                        )
                    }
                    DropdownMenuItem(
                        text = { 
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(18.dp))
                                Spacer(Modifier.width(8.dp))
                                Text("Altro...") 
                            }
                        },
                        onClick = { showOtherSkillField = !showOtherSkillField; expandedCompetenze = false }
                    )
                }
            }

            if (showOtherSkillField) {
                Spacer(modifier = Modifier.height(8.dp))
                Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    OutlinedTextField(
                        value = otherSkillText,
                        onValueChange = { otherSkillText = it },
                        label = { Text("Specifica altra competenza") },
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(12.dp),
                        singleLine = true
                    )
                    IconButton(onClick = {
                        if (otherSkillText.isNotBlank()) {
                            if (!selectedSkills.contains(otherSkillText.trim())) {
                                selectedSkills.add(otherSkillText.trim())
                            }
                            otherSkillText = ""
                            showOtherSkillField = false
                        }
                    }) {
                        Icon(Icons.Default.Check, contentDescription = "Aggiungi", tint = MaterialTheme.colorScheme.primary)
                    }
                }
            }

            if (selectedSkills.isNotEmpty()) {
                Spacer(modifier = Modifier.height(8.dp))
                FlowRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    selectedSkills.forEach { skill ->
                        InputChip(
                            selected = true,
                            onClick = { selectedSkills.remove(skill) },
                            label = { Text(skill, fontSize = 11.sp) },
                            trailingIcon = { Icon(Icons.Default.Close, contentDescription = null, modifier = Modifier.size(14.dp)) }
                        )
                    }
                }
            }

          }
          if (passo == 2) {
            Text(
                text = stringResource(Res.string.operating_area_title),
                style = MaterialTheme.typography.titleSmall,
                color = MaterialTheme.colorScheme.primary,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.align(Alignment.Start)
            )
            Spacer(modifier = Modifier.height(8.dp))

            // REGIONE OPERATIVITA
            ExposedDropdownMenuBox(
                expanded = expRegione,
                onExpandedChange = {
                    expRegione = !expRegione
                    campoAiuto = if (role.isProfessionista) "zona" else "indirizzo"
                    if (expRegione && viewModel.listaRegioni.isEmpty()) {
                        viewModel.loadRegioni()
                    }
                },
                modifier = Modifier.fillMaxWidth()
            ) {
                OutlinedTextField(
                    value = regione,
                    onValueChange = {},
                    readOnly = true,
                    label = { Text(stringResource(Res.string.region_label)) },
                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expRegione) },
                    modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth().campo("zona"),
                    shape = RoundedCornerShape(12.dp),
                    isError = inErrore("zona"),
                    supportingText = daCompilare("zona")
                )
                ExposedDropdownMenu(expanded = expRegione, onDismissRequest = { expRegione = false }) {
                    viewModel.listaRegioni.forEach { r ->
                        DropdownMenuItem(
                            text = { Text(r) },
                            onClick = {
                                regione = r
                                provincia = ""
                                citta = ""
                                cap = ""
                                viewModel.updateProvincePerRegione(r)
                                expRegione = false
                            }
                        )
                    }
                }
            }
            Spacer(modifier = Modifier.height(8.dp))

            // PROVINCIA E CITTA OPERATIVITA
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                ExposedDropdownMenuBox(
                    expanded = expProvincia,
                    onExpandedChange = { if (regione.isNotEmpty()) expProvincia = !expProvincia },
                    modifier = Modifier.weight(1f)
                ) {
                    OutlinedTextField(
                        value = provincia,
                        onValueChange = {},
                        readOnly = true,
                        enabled = regione.isNotEmpty(),
                        label = { Text(stringResource(Res.string.province_label)) },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expProvincia) },
                        modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    ExposedDropdownMenu(expanded = expProvincia, onDismissRequest = { expProvincia = false }) {
                        viewModel.listaProvince.forEach { p ->
                            DropdownMenuItem(
                                text = { Text(p) },
                                onClick = {
                                    provincia = p
                                    citta = ""
                                    cap = ""
                                    viewModel.updateComuniPerProvincia(p)
                                    expProvincia = false
                                }
                            )
                        }
                    }
                }

                ExposedDropdownMenuBox(
                    expanded = expComune,
                    onExpandedChange = { if (provincia.isNotEmpty()) expComune = !expComune },
                    modifier = Modifier.weight(2f)
                ) {
                    OutlinedTextField(
                        value = citta,
                        onValueChange = {},
                        readOnly = true,
                        enabled = provincia.isNotEmpty(),
                        label = { Text(stringResource(Res.string.city_label)) },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expComune) },
                        modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    ExposedDropdownMenu(expanded = expComune, onDismissRequest = { expComune = false }) {
                        viewModel.listaComuni.forEach { c ->
                            DropdownMenuItem(
                                text = { Text(c) },
                                onClick = {
                                    citta = c
                                    cap = ""
                                    viewModel.updateCapPerComune(c)
                                    expComune = false
                                }
                            )
                        }
                    }
                }
            }
            Spacer(modifier = Modifier.height(8.dp))

            // CAP OPERATIVITA
            ExposedDropdownMenuBox(
                expanded = expCap,
                onExpandedChange = { if (citta.isNotEmpty()) expCap = !expCap },
                modifier = Modifier.fillMaxWidth()
            ) {
                OutlinedTextField(
                    value = cap,
                    onValueChange = {},
                    readOnly = true,
                    enabled = citta.isNotEmpty(),
                    label = { Text(stringResource(Res.string.cap_label)) },
                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expCap) },
                    modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp)
                )
                ExposedDropdownMenu(expanded = expCap, onDismissRequest = { expCap = false }) {
                    viewModel.listaCap.forEach { cp ->
                        DropdownMenuItem(
                            text = { Text(cp) },
                            onClick = {
                                cap = cp
                                expCap = false
                            }
                        )
                    }
                }
            }
            Spacer(modifier = Modifier.height(16.dp))
            OutlinedTextField(value = telefono, onValueChange = { telefono = it }, label = { Text(stringResource(Res.string.phone_label)) }, keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone), modifier = Modifier.fillMaxWidth().campo("telefono"), shape = RoundedCornerShape(12.dp))
          }
        } else {
          if (passo == 0) {
            OutlinedTextField(value = nomeFarmacia, onValueChange = { nomeFarmacia = it }, label = { Text(stringResource(Res.string.pharmacy_name_label) + " *") }, modifier = Modifier.fillMaxWidth().campo("nomeFarmacia"), shape = RoundedCornerShape(12.dp), isError = inErrore("nomeFarmacia"), supportingText = daCompilare("nomeFarmacia"))
            Spacer(modifier = Modifier.height(8.dp))
            OutlinedTextField(value = ragioneSociale, onValueChange = { ragioneSociale = it }, label = { Text("Ragione Sociale (Fiscale)") }, modifier = Modifier.fillMaxWidth().campo("ragioneSociale"), shape = RoundedCornerShape(12.dp))
          }
          if (passo == 1) {
            OutlinedTextField(value = partitaIva, onValueChange = { partitaIva = it }, label = { Text(stringResource(Res.string.vat_number) + " *") }, modifier = Modifier.fillMaxWidth().campo("partitaIva"), shape = RoundedCornerShape(12.dp), isError = inErrore("partitaIva"), supportingText = daCompilare("partitaIva"))
            Spacer(modifier = Modifier.height(8.dp))
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedTextField(value = codiceSDI, onValueChange = { codiceSDI = it }, label = { Text("Codice SDI") }, modifier = Modifier.weight(0.5f).campo("sdi"), shape = RoundedCornerShape(12.dp))
                OutlinedTextField(value = pec, onValueChange = { pec = it }, label = { Text("PEC") }, modifier = Modifier.weight(1f).campo("pec"), shape = RoundedCornerShape(12.dp))
            }
            Spacer(modifier = Modifier.height(8.dp))
            OutlinedTextField(value = numeroIscrizioneOrdineDirettore, onValueChange = { numeroIscrizioneOrdineDirettore = it }, label = { Text(stringResource(Res.string.director_registration_label)) }, modifier = Modifier.fillMaxWidth().campo("direttore"), shape = RoundedCornerShape(12.dp))
          }
          if (passo == 2) {
            OutlinedTextField(value = indirizzo, onValueChange = { indirizzo = it }, label = { Text(stringResource(Res.string.address_label)) }, modifier = Modifier.fillMaxWidth().campo("indirizzo"), shape = RoundedCornerShape(12.dp), isError = inErrore("indirizzo"), supportingText = daCompilare("indirizzo"))
            Spacer(modifier = Modifier.height(8.dp))

            // REGIONE
            ExposedDropdownMenuBox(
                expanded = expRegione,
                onExpandedChange = {
                    expRegione = !expRegione
                    campoAiuto = if (role.isProfessionista) "zona" else "indirizzo"
                    if (expRegione && viewModel.listaRegioni.isEmpty()) {
                        viewModel.loadRegioni()
                    }
                },
                modifier = Modifier.fillMaxWidth()
            ) {
                OutlinedTextField(
                    value = regione,
                    onValueChange = {},
                    readOnly = true,
                    label = { Text(stringResource(Res.string.region_label)) },
                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expRegione) },
                    modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth().campo("zona"),
                    shape = RoundedCornerShape(12.dp),
                    isError = inErrore("zona"),
                    supportingText = daCompilare("zona")
                )
                ExposedDropdownMenu(expanded = expRegione, onDismissRequest = { expRegione = false }) {
                    viewModel.listaRegioni.forEach { r ->
                        DropdownMenuItem(
                            text = { Text(r) },
                            onClick = {
                                regione = r
                                provincia = ""
                                citta = ""
                                cap = ""
                                viewModel.updateProvincePerRegione(r)
                                expRegione = false
                            }
                        )
                    }
                }
            }
            Spacer(modifier = Modifier.height(8.dp))

            // PROVINCIA E CITTA (COMUNE)
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                // PROVINCIA
                ExposedDropdownMenuBox(
                    expanded = expProvincia,
                    onExpandedChange = { if (regione.isNotEmpty()) expProvincia = !expProvincia },
                    modifier = Modifier.weight(1f)
                ) {
                    OutlinedTextField(
                        value = provincia,
                        onValueChange = {},
                        readOnly = true,
                        enabled = regione.isNotEmpty(),
                        label = { Text(stringResource(Res.string.province_label)) },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expProvincia) },
                        modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    ExposedDropdownMenu(expanded = expProvincia, onDismissRequest = { expProvincia = false }) {
                        viewModel.listaProvince.forEach { p ->
                            DropdownMenuItem(
                                text = { Text(p) },
                                onClick = {
                                    provincia = p
                                    citta = ""
                                    cap = ""
                                    viewModel.updateComuniPerProvincia(p)
                                    expProvincia = false
                                }
                            )
                        }
                    }
                }

                // COMUNE (CITTA)
                ExposedDropdownMenuBox(
                    expanded = expComune,
                    onExpandedChange = { if (provincia.isNotEmpty()) expComune = !expComune },
                    modifier = Modifier.weight(2f)
                ) {
                    OutlinedTextField(
                        value = citta,
                        onValueChange = {},
                        readOnly = true,
                        enabled = provincia.isNotEmpty(),
                        label = { Text(stringResource(Res.string.city_label)) },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expComune) },
                        modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    ExposedDropdownMenu(expanded = expComune, onDismissRequest = { expComune = false }) {
                        viewModel.listaComuni.forEach { c ->
                            DropdownMenuItem(
                                text = { Text(c) },
                                onClick = {
                                    citta = c
                                    cap = ""
                                    viewModel.updateCapPerComune(c)
                                    expComune = false
                                }
                            )
                        }
                    }
                }
            }
            Spacer(modifier = Modifier.height(8.dp))

            // CAP
            ExposedDropdownMenuBox(
                expanded = expCap,
                onExpandedChange = { if (citta.isNotEmpty()) expCap = !expCap },
                modifier = Modifier.fillMaxWidth()
            ) {
                OutlinedTextField(
                    value = cap,
                    onValueChange = {},
                    readOnly = true,
                    enabled = citta.isNotEmpty(),
                    label = { Text(stringResource(Res.string.cap_label)) },
                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expCap) },
                    modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp)
                )
                ExposedDropdownMenu(expanded = expCap, onDismissRequest = { expCap = false }) {
                    viewModel.listaCap.forEach { cp ->
                        DropdownMenuItem(
                            text = { Text(cp) },
                            onClick = {
                                cap = cp
                                expCap = false
                            }
                        )
                    }
                }
            }
            Spacer(modifier = Modifier.height(8.dp))
            OutlinedTextField(value = telefono, onValueChange = { telefono = it }, label = { Text(stringResource(Res.string.phone_label)) }, keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone), modifier = Modifier.fillMaxWidth().campo("telefono"), shape = RoundedCornerShape(12.dp))
          }
        }

        if (passo == 3) {
            OutlinedTextField(value = email, onValueChange = { email = it }, label = { Text(stringResource(Res.string.email_label) + " *") }, keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email), modifier = Modifier.fillMaxWidth().campo("email"), shape = RoundedCornerShape(12.dp), isError = inErrore("email"), supportingText = daCompilare("email"))
            Spacer(modifier = Modifier.height(8.dp))
            CampoPassword(value = password, onValueChange = { password = it }, label = { Text(stringResource(Res.string.password_label) + " *") }, modifier = Modifier.fillMaxWidth().campo("password"), isError = inErrore("password"), supportingText = daCompilare("password"))
        }
        Spacer(modifier = Modifier.height(16.dp))

        if (viewModel.registrationSuccess) {
            // Farmy spiega cosa succede ora: l'account va approvato prima di poter entrare.
            val fine = RegistrazioneFarmy.fine(role)
            LaunchedEffect(Unit) { voceFarmy.speak(fine) }
            AlertDialog(
                onDismissRequest = { onBackToLogin() },
                confirmButton = {
                    Button(onClick = { onBackToLogin() }) {
                        Text(stringResource(Res.string.go_to_login))
                    }
                },
                icon = { AvatarAssistente(Modifier.size(48.dp)) },
                title = { Text(RegistrazioneFarmy.FINE_TITOLO) },
                text = { Text(fine) }
            )
        }

        if (passo == 3 && (errorMsgLocal != null || viewModel.errorMessage != null)) {
            Text(text = errorMsgLocal ?: viewModel.errorMessage!!, color = Color.Red, modifier = Modifier.padding(bottom = 8.dp))
        }

        // Indietro / Avanti: si va avanti solo con i dati obbligatori del passaggio; se manca
        // qualcosa lo dice Farmy nel suo riquadro.
        if (passo < 3) {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                if (passo > 0) {
                    OutlinedButton(
                        onClick = { cambiaPasso(passo - 1) },
                        modifier = Modifier.weight(1f).height(56.dp),
                        shape = RoundedCornerShape(12.dp)
                    ) { Text("INDIETRO", fontWeight = FontWeight.Bold) }
                }
                Button(
                    onClick = {
                        val manca = mancanti()
                        if (manca.isEmpty()) cambiaPasso(passo + 1) else segnalaMancanti(manca, perCompletare = false)
                    },
                    modifier = Modifier.weight(1f).height(56.dp),
                    shape = RoundedCornerShape(12.dp)
                ) { Text("AVANTI", fontWeight = FontWeight.Bold) }
            }
        } else {
            OutlinedButton(
                onClick = { cambiaPasso(passo - 1) },
                modifier = Modifier.fillMaxWidth().height(48.dp),
                shape = RoundedCornerShape(12.dp)
            ) { Text("INDIETRO", fontWeight = FontWeight.Bold) }
            Spacer(modifier = Modifier.height(8.dp))
        }

        if (passo == 3) ConsensiIscrizione(consensoMarketing) { consensoMarketing = it }

        if (passo == 3) Button(
            onClick = {
                errorMsgLocal = null
                val manca = mancanti()
                if (manca.isNotEmpty()) {
                    segnalaMancanti(manca, perCompletare = true)
                    return@Button
                }
                if (role.isProfessionista) {
                    val cfPuro = codiceFiscale.trim().uppercase().filter { it.isLetterOrDigit() }
                    if (cfPuro.length != 16) {
                        errorMsgLocal = taxCodeErrorLength
                        return@Button
                    }
                    if (!ItalianCfUtils.isCodiceFiscaleValido(cfPuro)) {
                        errorMsgLocal = taxCodeErrorInvalid
                        return@Button
                    }
                    
                    if (role == UserRole.INFERMIERE) {
                        viewModel.registerInfermiere(Infermiere(
                            nome = nome.trim(), cognome = cognome.trim(), sesso = sesso,
                            partitaIva = partitaIva.trim(), codiceFiscale = cfPuro,
                            numeroIscrizioneOpi = numeroIscrizioneOrdine.trim(),
                            dataNascita = dataNascita.trim(), luogoNascita = luogoNascita.trim(),
                            cittadinanza = cittadinanza.trim(), email = email.trim().lowercase(),
                            telefono = telefono.trim(), password = password.trim(),
                            regimeFiscale = regimeFiscale,
                            percEnpapi = percEnpaf.toDoubleOrNull() ?: 4.0,
                            regioneOperativita = regione, provinciaOperativita = provincia,
                            comuneOperativita = citta, capOperativita = cap,
                            competenze = selectedSkills.toList(),
                            polizzaRcNumero = polizzaRcNumero.trim().ifBlank { null },
                            polizzaRcScadenza = dataIso(polizzaRcScadenza),
                            consensoMarketing = consensoMarketing
                        ))
                        return@Button
                    }
                        viewModel.registerFarmacista(Farmacista(
                            nome = nome.trim(), cognome = cognome.trim(), sesso = sesso, 
                            partitaIva = partitaIva.trim(), codiceFiscale = cfPuro, 
                            numeroIscrizioneOrdine = numeroIscrizioneOrdine.trim(),
                            dataNascita = dataNascita.trim(), // prima veniva chiesta ma non salvata
                            luogoNascita = luogoNascita.trim(),
                            cittadinanza = cittadinanza.trim(), email = email.trim().lowercase(),
                            telefono = telefono.trim(), password = password.trim(),
                            regimeFiscale = regimeFiscale,
                            percEnpaf = percEnpaf.toDoubleOrNull() ?: 2.0,
                            regioneOperativita = regione, provinciaOperativita = provincia,
                            comuneOperativita = citta, capOperativita = cap,
                            competenze = selectedSkills.toList(),
                            consensoMarketing = consensoMarketing
                        ))
                } else {
                    viewModel.registerFarmacia(Farmacia(
                        nomeFarmacia = nomeFarmacia.trim(), ragioneSociale = ragioneSociale.trim(),
                        partitaIva = partitaIva.trim(), codiceSDI = codiceSDI.trim(), pec = pec.trim(),
                        numeroIscrizioneOrdineDirettore = numeroIscrizioneOrdineDirettore.trim(),
                        indirizzo = indirizzo.trim(), cap = cap.trim(), citta = citta.trim(), 
                        provincia = provincia.trim(), regione = regione.trim(), 
                        telefono = telefono.trim(), email = email.trim().lowercase(), password = password.trim(),
                        consensoMarketing = consensoMarketing
                    ))
                }
            },
            modifier = Modifier.fillMaxWidth().height(56.dp),
            enabled = !viewModel.isLoading, // cosa manca lo dice Farmy
            shape = RoundedCornerShape(12.dp)
        ) {
            if (viewModel.isLoading) {
                CircularProgressIndicator(color = Color.White, modifier = Modifier.size(24.dp))
            } else {
                Text(stringResource(Res.string.register_button_caps), fontWeight = FontWeight.Bold)
            }
        }

        TextButton(onClick = onBackToLogin) {
            Text(stringResource(Res.string.already_have_account_button))
        }
    }
}

/** Competenze proposte all'iscrizione e nel profilo, per professione. */
fun competenzeDi(ruolo: UserRole): List<String> = if (ruolo == UserRole.INFERMIERE) {
    listOf(
        "Medicazioni", "Medicazioni avanzate", "Iniezioni", "Prelievi capillari", "Elettrocardiogramma",
        "Holter pressorio", "Holter cardiaco", "Misurazione parametri", "Vaccinazione", "Educazione sanitaria", "BLS-D"
    )
} else {
    listOf("Elettrocardiogramma", "Tamponi", "Galenica", "Deblistering", "Gestione amministrativa", "Cosmesi", "Nutrizione", "Vaccinazione", "Omeopatia")
}

/** "31/12/2027" → "2027-12-31" (come la salva il server); già ISO resta così; vuoto o non valido → null. */
fun dataIso(testo: String): String? {
    val t = testo.trim()
    if (Regex("""\d{4}-\d{2}-\d{2}""").matches(t)) return t
    val parti = t.split("/", "-", ".").map { it.trim() }
    if (parti.size != 3) return null
    val (g, m, a) = parti
    if (g.toIntOrNull() == null || m.toIntOrNull() == null || a.length != 4 || a.toIntOrNull() == null) return null
    return "$a-${m.padStart(2, '0')}-${g.padStart(2, '0')}"
}

/** "2027-12-31" → "31/12/2027" per mostrarla. */
fun dataItaliana(iso: String?): String {
    val parti = iso?.split("-") ?: return ""
    return if (parti.size == 3) "${parti[2]}/${parti[1]}/${parti[0]}" else iso
}
